import axios from 'axios';
import { supabase, checkSupabaseHealth } from './supabaseClient';
import { v4 as uuidv4 } from 'uuid';
import { trackEvent } from './analyticsService';

const FREE_SAVED_TRANSLATIONS_CAP = 300;
const savedCountCache = new Map();
const SAVED_COUNT_CACHE_TTL_MS = 30000;

// Helper function to get or create a session ID
const getSessionId = () => {
    const newKey = 'lexory_session_id';
    const legacyKey = ['t', 'r', 'a', 'd', 'u', 'x'].join('') + '_session_id';

    let sessionId = localStorage.getItem(newKey) || localStorage.getItem(legacyKey);
    if (!sessionId) {
        sessionId = uuidv4();
    }

    localStorage.setItem(newKey, sessionId);
    return sessionId;
};

const getAuthHeaderValue = async () => {
    try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
            return null;
        }
        const token = data?.session?.access_token;
        if (!token) {
            return null;
        }
        return `Bearer ${token}`;
    } catch {
        return null;
    }
};

const translateText = async (text, sourceLang = 'auto', targetLang = 'en', options = {}) => {
    // Test mode flag - set to false to use the real API
    const useTestResponse = false;
    
    if (useTestResponse) {
        console.log('Using test translation response for:', text);
        // Return a mock translation to verify UI rendering
        return {
            translatedText: `Test translation of "${text}" from ${sourceLang} to ${targetLang}`,
            detectedSourceLanguage: sourceLang === 'auto' ? 'en' : sourceLang
        };
    }
    
    const safeWordCount = (input) => {
        if (!input) return 0;
        return String(input).trim().split(/\s+/).filter(Boolean).length;
    };

    const propsBase = {
        sourceLang,
        targetLang,
        chars: typeof text === 'string' ? text.length : null,
        words: safeWordCount(text),
        trigger: typeof options?.trigger === 'string' ? options.trigger : null,
        contentType: typeof options?.contentType === 'string' ? options.contentType : null,
    };

    trackEvent('translation_requested', propsBase);

    try {
        const sessionId = getSessionId();
        const authHeaderValue = await getAuthHeaderValue();

        const headers = {
            'Content-Type': 'application/json',
            'X-Session-Id': sessionId
        };
        if (authHeaderValue) {
            headers.Authorization = authHeaderValue;
        }

        const response = await axios.post('/api/translate', {
            text,
            sourceLang,
            targetLang
        }, { headers });

        const translatedText = response?.data?.translatedText;
        const detectedSourceLanguage = response?.data?.detectedSourceLanguage;

        if (!translatedText) {
            return { translatedText: 'Translation error: Unexpected response' };
        }

        const saveResult = await saveTranslation(
            text,
            translatedText,
            sourceLang === 'auto' ? detectedSourceLanguage : sourceLang,
            targetLang,
            response?.data?.usage?.tier || null
        );

        trackEvent('translation_succeeded', {
            ...propsBase,
            detectedSourceLanguage: detectedSourceLanguage || null,
            tier: response?.data?.usage?.tier || null,
        });

        return {
            translatedText,
            detectedSourceLanguage,
            usage: response?.data?.usage,
            saveResult
        };
    } catch (error) {
        const apiError = error?.response?.data;
        if (apiError?.code === 'QUOTA_EXCEEDED') {
            trackEvent('translation_quota_exceeded', { ...propsBase, tier: apiError?.tier || null });
            return {
                translatedText: 'Usage limit exceeded',
                error: 'Usage limit exceeded',
                limitExceeded: true,
                usage: apiError
            };
        }
        if (apiError?.code === 'HARD_LIMIT_EXCEEDED') {
            trackEvent('translation_hard_limit_exceeded', { ...propsBase, tier: apiError?.tier || null });
            return {
                translatedText: 'Usage limit exceeded',
                error: 'Usage limit exceeded',
                limitExceeded: true,
                usage: apiError
            };
        }
        if (apiError?.code === 'RATE_LIMITED') {
            trackEvent('translation_rate_limited', { ...propsBase, tier: apiError?.tier || null });
            return {
                translatedText: 'Too many requests',
                error: 'Too many requests',
                rateLimited: true,
                retryAfterSeconds: apiError.retryAfterSeconds
            };
        }
        if (apiError?.code === 'REQUEST_TOO_LARGE') {
            trackEvent('translation_request_too_large', { ...propsBase, tier: apiError?.tier || null });
            return {
                translatedText: 'Request too large',
                error: 'Request too large',
                requestTooLarge: true,
                usage: apiError
            };
        }

        trackEvent('translation_failed', propsBase);
        return { translatedText: 'Translation error occurred' };
    }
};

// Cache to prevent duplicate saves within a short time window
const recentSaves = new Map();
const DUPLICATE_PREVENTION_WINDOW = 3000; // 3 seconds

// Save translation pair for user's saved translations
const saveTranslation = async (originalText, translatedText, sourceLang, targetLang, tier = null) => {
    try {
        // Check Supabase health before attempting connection
        const isHealthy = await checkSupabaseHealth();
        if (!isHealthy) {
            console.log('Supabase unhealthy - skipping translation save');
            return { success: false, reason: 'unhealthy' };
        }

        // Normalize text inputs
        const normalizedOriginal = originalText.trim().toLowerCase();
        const normalizedTranslated = translatedText.trim().toLowerCase();

        // Create a unique key for this translation
        const translationKey = `${normalizedOriginal}_${normalizedTranslated}_${sourceLang}_${targetLang}`;
        const now = Date.now();
        
        // Check if we recently saved this exact translation
        if (recentSaves.has(translationKey)) {
            const lastSaveTime = recentSaves.get(translationKey);
            if (now - lastSaveTime < DUPLICATE_PREVENTION_WINDOW) {
                console.log('Duplicate translation save prevented (cache):', translationKey);
                return { success: false, reason: 'duplicate_cache' };
            }
        }

        const sessionId = getSessionId();
        let userId = null;
        let userEmail = null;
        
        try {
            const { data } = await supabase.auth.getUser();
            if (data && data.user) {
                userId = data.user.id;
                userEmail = data.user.email || null;
            }
        } catch (userError) {
            console.error('Error getting current user for translation save:', userError);
        }

        const effectiveTier = typeof tier === 'string' && tier ? tier : (userId ? 'premium' : 'free');
        const actorKey = userId ? `user:${userId}` : `anon:${sessionId}`;

        if (effectiveTier !== 'premium') {
            const nowMs = Date.now();
            const cached = savedCountCache.get(actorKey);
            if (cached && (nowMs - cached.ts) < SAVED_COUNT_CACHE_TTL_MS && typeof cached.count === 'number') {
                if (cached.count >= FREE_SAVED_TRANSLATIONS_CAP) {
                    return {
                        success: false,
                        reason: 'VOCAB_CAP_REACHED',
                        tier: effectiveTier,
                        limit: FREE_SAVED_TRANSLATIONS_CAP,
                    };
                }
            } else {
                let countQuery = supabase
                    .from('saved_translations')
                    .select('id', { count: 'exact', head: true });
                if (userId) {
                    countQuery = countQuery.eq('user_id', userId);
                } else {
                    countQuery = countQuery.eq('session_id', sessionId);
                }

                const { count, error: countError } = await countQuery;
                if (!countError && typeof count === 'number') {
                    savedCountCache.set(actorKey, { count, ts: nowMs });
                    if (count >= FREE_SAVED_TRANSLATIONS_CAP) {
                        return {
                            success: false,
                            reason: 'VOCAB_CAP_REACHED',
                            tier: effectiveTier,
                            limit: FREE_SAVED_TRANSLATIONS_CAP,
                        };
                    }
                }
            }
        }
        
        // Check for existing duplicate in database with exact match
        let duplicateQuery = supabase
            .from('saved_translations')
            .select('id, created_at')
            .ilike('original_text', originalText.trim())
            .ilike('translated_text', translatedText.trim())
            .eq('source_language', sourceLang)
            .eq('target_language', targetLang);

        // Filter by user/session
        if (userId) {
            duplicateQuery = duplicateQuery.eq('user_id', userId);
        } else {
            duplicateQuery = duplicateQuery.eq('session_id', sessionId);
        }

        const { data: existingTranslations, error: duplicateError } = await duplicateQuery;
        
        if (duplicateError) {
            console.error('Error checking for duplicates:', duplicateError);
        } else if (existingTranslations && existingTranslations.length > 0) {
            console.log('Translation already exists in database, skipping save');
            // Update the cache to prevent rapid re-attempts
            recentSaves.set(translationKey, now);
            return { success: false, reason: 'duplicate_database' };
        }
        
        // Insert translation pair into saved_translations table
        const { data, error } = await supabase
            .from('saved_translations')
            .insert([
                { 
                    session_id: sessionId,
                    user_id: userId,
                    user_email: userEmail,
                    original_text: originalText.trim(),
                    translated_text: translatedText.trim(),
                    source_language: sourceLang,
                    target_language: targetLang,
                    is_favorite: false,
                    created_at: new Date().toISOString()
                }
            ]);
        
        if (error) {
            console.error('Error saving translation:', error);
            return { success: false, reason: 'database_error', error };
        } else {
            console.log('Translation saved successfully:', data);

            trackEvent('translation_saved', {
                sourceLang,
                targetLang,
                actor: userId ? 'user' : 'anon',
            });

            // Cache this save to prevent duplicates
            recentSaves.set(translationKey, now);

            if (effectiveTier !== 'premium') {
                const cached = savedCountCache.get(actorKey);
                if (cached && typeof cached.count === 'number') {
                    savedCountCache.set(actorKey, { count: cached.count + 1, ts: Date.now() });
                }
            }
            
            // Clean up old cache entries periodically
            if (recentSaves.size > 50) {
                const cutoff = now - DUPLICATE_PREVENTION_WINDOW;
                for (const [key, timestamp] of recentSaves.entries()) {
                    if (timestamp < cutoff) {
                        recentSaves.delete(key);
                    }
                }
            }
            
            return { success: true, data };
        }
    } catch (error) {
        console.error('Failed to save translation:', error);
        return { success: false, reason: 'exception', error };
    }
};

export { translateText, saveTranslation };
